/**
 * TypeScript Type Definitions
 */

// User types
export interface User {
  id: string;
  email: string;
  /** Identity name from auth provider (registration/OAuth). For avatar display. */
  name?: string;
}

// Auth types
export interface AuthState {
  user: User | null;
  session: unknown | null;
  isAuthenticated: boolean;
  isLoading: boolean; // For initial auth check only
  isSubmitting: boolean; // For login/register/update operations
  error: string | null;
  login: (email: string, password: string) => Promise<void>;
  register: (email: string, password: string, name: string) => Promise<void>;
  logout: () => Promise<void>;
  resetPassword: (email: string) => Promise<void>;
  updatePassword: (newPassword: string) => Promise<void>;
  updateProfile: (data: { name?: string }) => Promise<void>;
  clearError: () => void;
}

// API Response types
export interface ApiError {
  error: string;
  details?: unknown;
}

export interface SpeechToken {
  token: string;
  region: string;
  expiresIn: number;
}

// Lesson types
export interface Lesson {
  id: string;
  title: string;
  description: string;
  level: string;
  duration: number;
  content: unknown;
}

// Exercise types
export interface Exercise {
  id: string;
  lessonId: string;
  type: string;
  targetPhrase: string;
  audioUrl?: string;
  instructions?: string;
}

// Progress types
export interface UserProgress {
  id: string;
  userId: string;
  lessonId: string;
  completed: boolean;
  score?: number;
  completedAt?: string;
}

// Pronunciation Assessment types
export interface PronunciationAssessment {
  id: string;
  userId: string;
  exerciseId: string;
  pronunciationScore: number;
  accuracyScore: number;
  fluencyScore: number;
  completenessScore: number;
  recognizedText: string;
  assessmentResult: unknown;
  createdAt: string;
}

// Subscription types
export type SubscriptionPlan = 'free' | 'trial' | 'pro';
export type SubscriptionStatus = 'trial' | 'active' | 'canceled' | 'expired' | 'free';

export interface SubscriptionInfo {
  plan: SubscriptionPlan;
  status: SubscriptionStatus;
  isActive: boolean;
  trialDaysLeft?: number;
  expiresAt?: string;
}

export interface SubscriptionState {
  subscription: SubscriptionInfo | null;
  isLoading: boolean;
  error: string | null;
  fetchSubscription: () => Promise<void>;
  clearError: () => void;
}

// Feature limits
export interface FeatureLimits {
  pronunciationAssessments: number;
  aiConversations: number;
  lessons: number;
  detailedFeedback: boolean;
  /**
   * Audio Lessons (podcast-style background player). Intended as a Pro
   * capability, but TEMPORARILY FREE FOR ALL while RevenueCat entitlement
   * testing is set up — see FREE_LIMITS. Flip `FREE_LIMITS.audioLessons` back to
   * `false` to re-enable the Pro gate (the `FeatureGate feature="audioLessons"`
   * wrapper in AudioPlayerScreen stays wired the whole time).
   */
  audioLessons: boolean;
}

export const FREE_LIMITS: FeatureLimits = {
  pronunciationAssessments: 3,
  aiConversations: 0,
  lessons: 2,
  detailedFeedback: false,
  // TEMP: Pro gate disabled — Audio Lessons is free for everyone until
  // RevenueCat testing is in place. Set to `false` to re-gate behind Pro.
  audioLessons: true,
};

export const PRO_LIMITS: FeatureLimits = {
  pronunciationAssessments: Infinity,
  aiConversations: Infinity,
  lessons: Infinity,
  detailedFeedback: true,
  audioLessons: true,
};

// Conversation types
export * from './conversation';

// Lesson system types
export * from './lesson';
